import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, catchError, switchMap, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AuthService } from '../../auth/auth.service';
import { ToasterService } from '../../providers/toaster/toaster.service';

@Injectable({
  providedIn: 'root'
})
export class AssociateService {
  private apiUrl = environment.apiUrl;

  /**
   * master/get-client/{id}
   */
  getClientById(id: number): Observable<any> {
    return this.authService.getHeader().pipe(
      switchMap((headers: HttpHeaders) => {
        const reqHeaders =
          typeof headers.delete === 'function' ? headers.delete('Content-Type') : headers;
        return this.http.get(`${this.apiUrl}master/get-client/${id}`, {
          headers: reqHeaders,
          observe: 'body'
        });
      }),
      catchError((error) => {
        if (error?.status === 401) {
          this.toasterService.setToaster('error', 'Error', 'Session expired');
        }
        return throwError(() => error);
      })
    );
  }

  /**
   * master/update-client/{id}
   */
  updateClient(id: number, data: any): Observable<any> {
    return this.authService.getHeader().pipe(
      switchMap((headers: HttpHeaders) => {
        const isFormData = typeof FormData !== 'undefined' && data instanceof FormData;
        const reqHeaders =
          typeof headers.delete === 'function' ? headers.delete('Content-Type') : headers;

        return this.http.post(`${this.apiUrl}master/update-client/${id}`, data, {
          headers: reqHeaders,
          observe: 'response'
        });
      }),
      catchError((error) => {
        if (error?.status === 401) {
          this.toasterService.setToaster('error', 'Error', 'Session expired');
        }
        return throwError(() => error);
      })
    );
  }

  constructor(
    private http: HttpClient,
    private authService: AuthService,
    private toasterService: ToasterService
  ) {}

  getAllAssociates(): Observable<any> {
    return this.authService.getHeader().pipe(
      switchMap((headers: HttpHeaders) => {
        let reqHeaders = headers;
        if (typeof headers.delete === 'function') {
          reqHeaders = headers.delete('Content-Type');
        }

        return this.http.get(`${this.apiUrl}master/get-all-clients`, {
          headers: reqHeaders,
          observe: 'body'
        });
      }),
      catchError((error) => {
        if (error?.status === 401) {
          this.toasterService.setToaster('error', 'Error', 'Session expired');
        }
        return throwError(() => error);
      })
    );
  }

  createAssociate(data: any): Observable<any> {
    return this.authService.getHeader().pipe(
      switchMap((headers: HttpHeaders) => {
        // If payload is FormData, browser must set Content-Type boundary automatically.
        const isFormData = typeof FormData !== 'undefined' && data instanceof FormData;

        let reqHeaders = headers;
        if (typeof headers.delete === 'function') {
          reqHeaders = headers.delete('Content-Type');
        }

        return this.http.post(`${this.apiUrl}master/create-client`, data, {
          headers: isFormData ? reqHeaders : reqHeaders,
          observe: 'response'
        });
      }),
      catchError((error) => {
        if (error?.status === 401) {
          this.toasterService.setToaster('error', 'Error', 'Session expired');
        }
        return throwError(() => error);
      })
    );
  }

  changePassword(payload: any): Observable<any> {
    return this.authService.getHeader().pipe(
      switchMap((headers: HttpHeaders) => {
        const reqHeaders =
          typeof headers.delete === 'function' ? headers.delete('Content-Type') : headers;
        return this.http.post(`${this.apiUrl}client/change-password`, payload, {
          headers: reqHeaders,
          observe: 'response'
        });
      }),
      catchError((error) => {
        if (error?.status === 401) {
          this.toasterService.setToaster('error', 'Error', 'Session expired');
        }
        return throwError(() => error);
      })
    );
  }

  changePasswordWithClientDetails(clientId: number, payload: any): Observable<any> {
    return this.getClientById(clientId).pipe(
      switchMap((clientRes: any) => {
        const clientData = clientRes?.data ?? {};
        const id = clientData?.User_id ?? 0;
        const role_id = clientData?.role_id ?? 0;
        const profile_id = clientData?.profile_id ?? 0;

        const enrichedPayload = { 
          ...payload,
          id,
          role_id,
          profile_id
        };

        return this.changePassword(enrichedPayload);
      }),
      catchError((error) => {
        if (error?.status === 401) {
          this.toasterService.setToaster('error', 'Error', 'Session expired');
        }
        return throwError(() => error);
      })
    );
  }

  deleteClientById(id: number): Observable<any> {
    return this.authService.getHeader().pipe(
      switchMap((headers: HttpHeaders) => {
        const reqHeaders =
          typeof headers.delete === 'function' ? headers.delete('Content-Type') : headers;

        return this.http.get(`${this.apiUrl}client/${id}`, {
          headers: reqHeaders,
          observe: 'response'
        });
      }),
      catchError((error) => {
        if (error?.status === 401) {
          this.toasterService.setToaster('error', 'Error', 'Session expired');
        }
        return throwError(() => error);
      })
    );
  }
}


